import { useState } from "react";
import { Button, ButtonVariant, Text, TextField, Toggle } from "@merge-api/merge-javascript-shared";
import { Check } from "lucide-react";
import { Plan } from "../types";
import { fullPlanMode } from "../helpers";
import { showErrorToast, showSuccessToast } from "../../shared/Toasts";
import { conductorPost } from "../conductorApi";
import ConductorDrawer from "./ConductorDrawer";
import { FieldLabel, Section, Select } from "./ConductorFormFields";

interface Props {
  integrationId: string;
  /** Explicit category (e.g. from a parent playbook). Takes priority over categories. */
  category?: string;
  /** Available categories for the integration. When >1, the user must pick one. */
  categories?: string[];
  onClose: () => void;
  onCreated: (planId: string | null) => void;
}

const MODES = [
  { value: "RUN_ALL" as const, description: "Runs every eligible step in parallel." },
  {
    value: "RUN_ONE" as const,
    description: "Runs only the highest-priority matching step, like a router.",
  },
];

// Creates a new (step-less) plan for the integration. Steps are added afterward
// in the plan detail view; this just establishes the plan and its settings.
const PlanEditorDrawer = ({ integrationId, category, categories, onClose, onCreated }: Props) => {
  // Resolve the effective category: explicit prop > single available category > user choice.
  const resolvedCategory = category ?? (categories?.length === 1 ? categories[0] : undefined);
  const needsCategoryChoice = !resolvedCategory && !!categories && categories.length > 1;

  const [name, setName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [mode, setMode] = useState<"RUN_ALL" | "RUN_ONE">("RUN_ALL");
  const [maxConcurrent, setMaxConcurrent] = useState("");
  const [strictPriority, setStrictPriority] = useState(false);
  const [sequentialSuccess, setSequentialSuccess] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const effectiveCategory = resolvedCategory ?? selectedCategory;
  const isRunOne = mode === "RUN_ONE";
  const canSave = name.trim().length > 0 && !!effectiveCategory && !isSaving;

  const save = async () => {
    setIsSaving(true);
    const { ok, data } = await conductorPost<Plan>("/conductor/plans", {
      integration_id: integrationId,
      category: effectiveCategory,
      name: name.trim(),
      mode: fullPlanMode(mode),
      max_concurrent_steps: maxConcurrent === "" ? null : Number(maxConcurrent),
      enforce_strict_priority_ordering: isRunOne ? false : strictPriority,
      require_sequential_success: isRunOne ? false : sequentialSuccess,
      enabled,
      steps: [],
    });
    setIsSaving(false);
    if (ok) {
      showSuccessToast("Plan created");
      onCreated(data?.id ?? null);
    } else {
      showErrorToast("Failed to create plan");
    }
  };

  return (
    <ConductorDrawer
      title="New plan"
      onClose={onClose}
      footer={
        <>
          <span />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={ButtonVariant.SecondaryGray}
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant={ButtonVariant.PrimaryGray}
              leftIcon={<Check size={14} />}
              onClick={save}
              disabled={!canSave}
            >
              Create plan
            </Button>
          </div>
        </>
      }
    >
      <Section title="Plan">
        <div className="mb-4">
          <FieldLabel>Category</FieldLabel>
          {needsCategoryChoice ? (
            <Select value={selectedCategory} onChange={setSelectedCategory}>
              <option value="">Select a category…</option>
              {categories!.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </Select>
          ) : (
            <Text variant="md" className="font-mono text-gray-80">
              {effectiveCategory || "-"}
            </Text>
          )}
        </div>

        <div className="mb-4">
          <FieldLabel>Name</FieldLabel>
          <TextField
            className="bg-white"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. SharePoint sync"
          />
        </div>

        <div className="mb-4">
          <FieldLabel>Mode</FieldLabel>
          <div className="flex flex-col gap-2">
            {MODES.map((option) => {
              const active = mode === option.value;
              return (
                <button
                  key={option.value}
                  onClick={() => setMode(option.value)}
                  className={`rounded-md border px-3 py-2 text-left ${
                    active ? "border-gray-40 bg-gray-0" : "border-gray-20 hover:bg-gray-0"
                  }`}
                >
                  <Text variant="md" className="font-mono font-semibold">
                    {option.value}
                  </Text>
                  <Text variant="sm" className="block text-gray-60">
                    {option.description}
                  </Text>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <FieldLabel>Enabled</FieldLabel>
          <Toggle checked={enabled} onChange={setEnabled} />
        </div>
      </Section>

      {!isRunOne && (
        <Section title="Concurrency & priority">
          <div className="mb-4">
            <FieldLabel>Max concurrent steps</FieldLabel>
            <div className="w-28">
              <TextField
                className="bg-white"
                type="number"
                value={maxConcurrent}
                onChange={(e) => setMaxConcurrent(e.target.value)}
                placeholder="none"
              />
            </div>
          </div>
          <div className="mb-4 flex items-center justify-between gap-4">
            <div>
              <FieldLabel>Strict priority ordering</FieldLabel>
              <Text variant="sm" className="block text-gray-50">
                Finish each priority tier before starting the next.
              </Text>
            </div>
            <Toggle checked={strictPriority} onChange={setStrictPriority} />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <FieldLabel>Require sequential success</FieldLabel>
              <Text variant="sm" className="block text-gray-50">
                Skip a tier if any lower-priority step failed.
              </Text>
            </div>
            <Toggle checked={sequentialSuccess} onChange={setSequentialSuccess} />
          </div>
        </Section>
      )}
    </ConductorDrawer>
  );
};

export default PlanEditorDrawer;
